import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import PathFitDashboard from '../components/paths/PathFitDashboard';
import { Button } from '../components/ui/button';
import { 
  Briefcase, Users, TrendingUp, DollarSign, Clock, Plus,
  FileText, Target, Award, BookOpen, ExternalLink, CheckCircle,
  MapPin, Search, Star, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Entry-level career paths that don't require a 4-year degree
const WORKFORCE_PATHS = [
  {
    id: 'customer_service',
    title: 'Customer Service',
    icon: Users,
    color: 'blue',
    entrySalary: '$32,000 - $45,000',
    advancedSalary: '$50,000 - $70,000 (Team Lead/Manager)',
    description: 'Help customers solve problems and provide excellent service across various industries.',
    skills: ['Communication', 'Problem Solving', 'Patience', 'Computer Skills', 'Empathy'],
    companies: ['Amazon', 'Apple', 'Bank of America', 'Verizon', 'USAA'],
    growthPath: 'Rep → Senior Rep → Team Lead → Manager → Director',
  },
  {
    id: 'sales',
    title: 'Sales',
    icon: TrendingUp,
    color: 'green',
    entrySalary: '$35,000 - $50,000 + Commission',
    advancedSalary: '$80,000 - $150,000+ (Account Executive)',
    description: 'Help businesses and consumers find products and services that meet their needs.',
    skills: ['Communication', 'Persuasion', 'Resilience', 'Product Knowledge', 'CRM Tools'],
    companies: ['Salesforce', 'Oracle', 'Enterprise', 'InsurTech', 'SaaS Companies'],
    growthPath: 'SDR → Account Executive → Senior AE → Sales Manager → VP Sales',
  },
  {
    id: 'admin',
    title: 'Administrative Assistant',
    icon: FileText,
    color: 'purple',
    entrySalary: '$35,000 - $45,000',
    advancedSalary: '$55,000 - $80,000 (Executive Assistant)',
    description: 'Provide organizational support, manage schedules, and keep offices running smoothly.',
    skills: ['Organization', 'Microsoft Office', 'Communication', 'Time Management', 'Discretion'],
    companies: ['Corporate offices', 'Law firms', 'Healthcare', 'Government', 'Non-profits'],
    growthPath: 'Admin Assistant → Senior Admin → Executive Assistant → Office Manager',
  },
  {
    id: 'healthcare_support',
    title: 'Healthcare Support',
    icon: Target,
    color: 'red',
    entrySalary: '$30,000 - $40,000',
    advancedSalary: '$45,000 - $60,000 (Senior/Specialized)',
    description: 'Support healthcare professionals and patients in clinical and administrative roles.',
    skills: ['Attention to Detail', 'Compassion', 'Medical Terminology', 'HIPAA', 'Computer Skills'],
    companies: ['Hospitals', 'Clinics', 'Insurance Companies', 'Pharmacies', 'Telehealth'],
    growthPath: 'Medical Assistant → Senior MA → Practice Manager → Administrator',
  },
  {
    id: 'logistics',
    title: 'Logistics & Warehouse',
    icon: Briefcase,
    color: 'amber',
    entrySalary: '$35,000 - $45,000',
    advancedSalary: '$55,000 - $80,000 (Operations Manager)',
    description: 'Manage the flow of goods from suppliers to customers in the supply chain.',
    skills: ['Organization', 'Physical Fitness', 'Attention to Detail', 'Forklift Operation', 'Inventory Systems'],
    companies: ['Amazon', 'UPS', 'FedEx', 'Walmart', 'Target'],
    growthPath: 'Associate → Lead → Supervisor → Area Manager → Operations Manager',
  },
  {
    id: 'tech_support',
    title: 'IT Help Desk',
    icon: BookOpen,
    color: 'cyan',
    entrySalary: '$40,000 - $55,000',
    advancedSalary: '$65,000 - $90,000 (IT Specialist/Admin)',
    description: 'Provide technical support and troubleshoot computer and software issues.',
    skills: ['Technical Aptitude', 'Problem Solving', 'Communication', 'Patience', 'Documentation'],
    companies: ['MSPs', 'Corporate IT', 'Tech Companies', 'Healthcare IT', 'Government'],
    growthPath: 'Help Desk → Tier 2 → System Admin → IT Manager → IT Director',
  },
];

// Job search resources
const JOB_RESOURCES = [
  { name: 'Indeed', url: 'https://www.indeed.com', desc: 'Largest job board' },
  { name: 'LinkedIn Jobs', url: 'https://www.linkedin.com/jobs', desc: 'Professional network' },
  { name: 'Glassdoor', url: 'https://www.glassdoor.com', desc: 'Reviews + jobs' },
  { name: 'ZipRecruiter', url: 'https://www.ziprecruiter.com', desc: 'AI job matching' },
  { name: 'USAJobs', url: 'https://www.usajobs.gov', desc: 'Government jobs' },
  { name: 'Handshake', url: 'https://www.joinhandshake.com', desc: 'Entry-level focus' },
];

export default function DirectWorkforcePage() {
  const { user, getAuthHeader } = useAuth();
  const [selectedPath, setSelectedPath] = useState(null);
  const [activeTab, setActiveTab] = useState('paths');

  const addToGoals = async (item, category = 'career') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item.title || item,
        description: item.description || `Career goal from Direct to Workforce`,
        category: category,
        priority: 'medium',
        status: 'backlog',
        source: 'Direct to Workforce'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Direct to Workforce"
        description="Start earning right after high school, retail, logistics, customer support, tech ops, and more."
      />

      {/* Fit dashboard, is going direct-to-workforce a good match? */}
      <PathFitDashboard
        pathLabel="going direct to workforce"
        fit={{ overall: 66, career: 60, financial: 90, timeline: 95 }}
        reasons={['Earning immediately', 'No student debt', '2-3× salary growth possible within 5 years', 'Career progression through certs']}
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'paths', label: 'Career Paths', icon: Target },
          { id: 'jobs', label: 'Jobs & Opportunities', icon: Briefcase },
          { id: 'prep', label: 'Job Prep', icon: FileText },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-300/60 bg-cyan-300/15 text-white'
                  : 'border-white/12 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Career Paths Tab */}
      {activeTab === 'paths' && (
        <>
          {/* Quick Stats, brand cyan/violet */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <GlassCard className="text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-cyan-300" />
              <p className="text-2xl font-bold text-white">Now</p>
              <p className="text-xs text-slate-300">Start earning immediately</p>
            </GlassCard>
            <GlassCard className="text-center">
              <DollarSign className="w-6 h-6 mx-auto mb-2 text-violet-300" />
              <p className="text-2xl font-bold text-white">$36k+</p>
              <p className="text-xs text-slate-300">Median entry-level (BLS 2024)</p>
            </GlassCard>
            <GlassCard className="text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-sky-300" />
              <p className="text-2xl font-bold text-white">2-3×</p>
              <p className="text-xs text-slate-300">Salary within 5 years</p>
            </GlassCard>
            <GlassCard className="text-center">
              <Award className="w-6 h-6 mx-auto mb-2 text-pink-300" />
              <p className="text-2xl font-bold text-white">OJT</p>
              <p className="text-xs text-slate-300">On-the-job training</p>
            </GlassCard>
          </div>

          {/* Paths Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WORKFORCE_PATHS.map(path => {
              const Icon = path.icon;
              return (
                <GlassCard 
                  key={path.id}
                  className={`cursor-pointer transition-all hover:scale-[1.02] ${
                    selectedPath?.id === path.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => setSelectedPath(selectedPath?.id === path.id ? null : path)}
                >
                  <div className={`w-12 h-12 bg-${path.color}-500/20 rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 text-${path.color}-400`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-1">{path.title}</h3>
                  <p className="text-slate-300 text-sm mb-4">{path.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Entry Salary:</span>
                      <span className="text-emerald-400">{path.entrySalary}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Advanced:</span>
                      <span className="text-blue-400">{path.advancedSalary}</span>
                    </div>
                  </div>
                  
                  {selectedPath?.id === path.id && (
                    <div className="mt-4 pt-4 border-t border-navy-700 space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Key Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {path.skills.map(skill => (
                            <span key={skill} className="px-2 py-1 bg-navy-700 text-slate-300 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Career Path:</h4>
                        <p className="text-xs text-slate-300 bg-navy-900/50 p-2 rounded">{path.growthPath}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Top Employers:</h4>
                        <div className="flex flex-wrap gap-2">
                          {path.companies.map(company => (
                            <span key={company} className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                              {company}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          addToGoals(path);
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add to My Goals
                      </Button>
                    </div>
                  )}
                </GlassCard>
              );
            })}
          </div>
        </>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="space-y-6">
          {/* Job Search Sites */}
          <GlassCard>
            <div className="flex items-center gap-3 mb-4">
              <Search className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-bold text-white">Job Search Resources</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {JOB_RESOURCES.map(resource => (
                <a
                  key={resource.name}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{resource.name}</span>
                    <span className="text-slate-300 text-sm block">{resource.desc}</span>
                  </div>
                  <ExternalLink className="w-5 h-5 text-blue-400" />
                </a>
              ))}
            </div>
          </GlassCard>

          {/* Link to Jobs Page */}
          <GlassCard className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/30">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">
                  <Briefcase className="inline w-5 h-5 mr-2 text-blue-400" />
                  AI-Powered Job Matching
                </h3>
                <p className="text-slate-300 mb-4">
                  Let our AI find jobs that match your skills, interests, and career goals.
                </p>
                <Link to="/app/jobs">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Find Jobs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </GlassCard>

          {/* Tips */}
          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard>
              <Star className="w-8 h-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Stand Out Tips</h3>
              <ul className="space-y-2">
                {[
                  'Tailor your resume for each application',
                  'Include relevant keywords from job description',
                  'Highlight transferable skills',
                  'Add numbers to show impact',
                  'Follow up within 1 week',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </GlassCard>
            
            <GlassCard>
              <MapPin className="w-8 h-8 text-red-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Hot Job Markets</h3>
              <ul className="space-y-2">
                {[
                  { city: 'Austin, TX', growth: '+15%' },
                  { city: 'Raleigh, NC', growth: '+12%' },
                  { city: 'Phoenix, AZ', growth: '+11%' },
                  { city: 'Nashville, TN', growth: '+10%' },
                  { city: 'Denver, CO', growth: '+9%' },
                ].map((market, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{market.city}</span>
                    <span className="text-green-400">{market.growth}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>
        </div>
      )}

      {/* Job Prep Tab */}
      {activeTab === 'prep' && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <FileText className="w-10 h-10 text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Resume Tips</h3>
            <ul className="space-y-3">
              {[
                { title: 'Keep it to 1 page', desc: 'For entry-level, less is more' },
                { title: 'Use action verbs', desc: 'Led, Created, Managed, Achieved' },
                { title: 'Quantify results', desc: '"Increased sales by 20%" not "Improved sales"' },
                { title: 'Include relevant coursework', desc: 'Classes that relate to the job' },
                { title: 'Add volunteer work', desc: 'Shows character and initiative' },
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3 p-3 bg-navy-900/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <span className="text-white font-medium">{tip.title}</span>
                    <span className="text-slate-300 text-sm block">{tip.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => addToGoals({ title: 'Update Resume', description: 'Polish resume with best practices' }, 'personal')}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add "Update Resume" to Goals
            </Button>
          </GlassCard>
          
          <GlassCard>
            <Users className="w-10 h-10 text-purple-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Interview Prep</h3>
            <ul className="space-y-3">
              {[
                { title: 'Research the company', desc: 'Know their mission, products, news' },
                { title: 'Prepare STAR stories', desc: 'Situation, Task, Action, Result format' },
                { title: 'Practice common questions', desc: '"Tell me about yourself", "Why us?"' },
                { title: 'Prepare questions to ask', desc: 'Shows interest and preparation' },
                { title: 'Follow up with thank you', desc: 'Email within 24 hours' },
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3 p-3 bg-navy-900/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <span className="text-white font-medium">{tip.title}</span>
                    <span className="text-slate-300 text-sm block">{tip.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => addToGoals({ title: 'Practice Interview Skills', description: 'Prepare STAR stories and common questions' }, 'personal')}
              className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add "Interview Prep" to Goals
            </Button>
          </GlassCard>
          
          {/* Free Resources */}
          <GlassCard className="md:col-span-2">
            <BookOpen className="w-10 h-10 text-amber-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-4">Free Career Resources</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { name: 'Canva Resume Builder', url: 'https://www.canva.com/resumes/', desc: 'Free templates' },
                { name: 'Big Interview', url: 'https://biginterview.com', desc: 'Practice interviews' },
                { name: 'LinkedIn Learning', url: 'https://www.linkedin.com/learning/', desc: 'Skill courses' },
                { name: 'Coursera Career Courses', url: 'https://www.coursera.org/browse/business/career-development', desc: 'Free audit' },
                { name: 'Google Career Certificates', url: 'https://grow.google/certificates/', desc: 'Industry certs' },
                { name: 'Jobscan', url: 'https://www.jobscan.co', desc: 'ATS resume check' },
              ].map(resource => (
                <a
                  key={resource.name}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                >
                  <div>
                    <span className="text-white text-sm">{resource.name}</span>
                    <span className="text-slate-300 text-xs block">{resource.desc}</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-blue-400" />
                </a>
              ))}
            </div>
          </GlassCard>
        </div>
      )}
    </AppLayout>
  );
}
